import { Component, OnInit, ViewChild } from '@angular/core';
import { Segment } from '@beego/ngx-segment';
import { UiTDatabaseLabelComponent } from '@internal-components/administration-components/uit-database-label-components/uit-database-label-component/uit-database-label.component';
import { OrganizeFormFactory } from '@internal-factories/forms/organizer-form.factory';
import { TenantFormFactory } from '@internal-factories/forms/tenant-form.factory';
import { IUserForm, UserFormFactory } from '@internal-factories/forms/user-form.factory';
import { CommunicationTemplatesTableFactory } from '@internal-factories/tables/communication-templates-table.factory';
import { CommunicationMessageType, ICommunicationTemplate } from '@internal-models/communication-template.model';
import { ILoanContractTemplate } from '@internal-models/loan-contract-template.model';
import { ITenant } from '@internal-models/tenant.model';
import { IUiTDatabaseLabel } from '@internal-models/uit-database-label.model';
import { IUiTDatabaseOrganizer } from '@internal-models/uit-database.model';
import { CommunicationTemplateProvider } from '@internal-providers/communication-templates.provider';
import { LoanContractTemplateProvider } from '@internal-providers/loan-contract-template.provider';
import { TenantProvider } from '@internal-providers/tenant.provider';
import { UiTDatabaseLabelProvider } from '@internal-providers/uit-database-label.provider';
import { UiTDatabaseProvider } from '@internal-providers/uit-database.provider';
import { EditorComponent } from '@internal/gui/structural-components/editor-component/editor.component';
import { Action, ErrorType, NavigationType, UserPermissions } from '@shared-libs/enums';
import { formIsValid, getSegmentName, translate, translateSegments } from '@shared-libs/helpers';
import { IFormGroup } from '@shared-libs/interfaces';
import { UserDetailsManager } from '@shared-managers/user-details.manager';
import { OrganizerNamePipe } from '@shared-pipes/organizer-name.pipe';
import { SharedUserProvider } from '@shared-providers/api/user.provider';
import { ErrorService } from '@shared-services/error.service';
import { FileService } from '@shared-services/file.service';
import { MessageService } from '@shared-services/message.service';
import { NavigationService } from '@shared-services/navigation.service';
import { ValidationService } from '@shared-services/validation.service';
import { cloneDeep, groupBy, omit, orderBy, remove } from 'lodash';

@Component({
	selector: 'tenant-desktop-page',
	templateUrl: 'tenant.desktop.page.html',
	styleUrls: ['./tenant.desktop.page.scss'],
})
export class TenantDesktopPage implements OnInit {
	@ViewChild(EditorComponent) private readonly editor: EditorComponent;

	public tenant: ITenant;
	public tenantForm: IFormGroup<ITenant>;
	public groupedCommunicationTemplates: {
		lesson_reservation: { [type: string]: ICommunicationTemplate[] }[];
		reservation: { [type: string]: ICommunicationTemplate[] }[];
		rating: { [type: string]: ICommunicationTemplate[] }[];
		beego_appointment: { [type: string]: ICommunicationTemplate[] }[];
		loan: { [type: string]: ICommunicationTemplate[] }[];
	};
	public permissionGroupForm: IFormGroup<IUserForm>;
	public organizerForm: IFormGroup<{ name: string; website: string }>;
	public organizerImageForm: IFormGroup<{ image: any }>;
	public uitDatabaseLabels: Array<IUiTDatabaseLabel>;
	public templates: Array<ICommunicationTemplate>;
	public loanContractTemplate: { template: ILoanContractTemplate; variables: Array<string> };
	public hasSearchedUiTDatabaseOrganizer: boolean;
	private readonly tenantFormData: FormData = new FormData();
	private readonly organizerFormData: FormData = new FormData();

	public readonly UserPermissions: typeof UserPermissions = UserPermissions;
	public readonly tableHeaders = CommunicationTemplatesTableFactory.getHeaders();
	public readonly segments: Array<Segment> = translateSegments([
		'TENANT_DATA',
		'COMMUNICATION_TEMPLATES',
		'LOAN_CONTRACT_TEMPLATE',
		'PERMISSION_GROUPS',
		'UIT_DATABASE',
	]);
	public selectedSegment: string = getSegmentName(this.segments[0]);
	public getSegmentName = getSegmentName;

	constructor(
		private readonly provider: TenantProvider,
		private readonly messageService: MessageService,
		private readonly fileService: FileService,
		private readonly userProvider: SharedUserProvider,
		private readonly usersManager: UserDetailsManager,
		private readonly navigationService: NavigationService,
		private readonly communicationTemplateProvider: CommunicationTemplateProvider,
		private readonly loanContractTemplateProvider: LoanContractTemplateProvider,
		private readonly organizerNamePipe: OrganizerNamePipe,
		private readonly uiTDatabaseProvider: UiTDatabaseProvider,
		private readonly validationService: ValidationService,
		private readonly errorService: ErrorService,
		private readonly uitDatabaseLabelProvider: UiTDatabaseLabelProvider
	) {}

	public async ngOnInit(): Promise<void> {
		this.navigationService.onNavigateToCurrentPage().subscribe(async () => {
			await this.getAllCommunicationTemplates();
		});

		void this.getUiTDatabaseLabels();
		this.tenant = await this.provider.getOne();
		this.tenantForm = TenantFormFactory.create(this.tenant);
		this.permissionGroupForm = UserFormFactory.create();
		this.organizerForm = OrganizeFormFactory.create();
		this.organizerImageForm = OrganizeFormFactory.createForImage(this.tenant.Organizer?.imageUrl);
		this.getLoanContractTemplate();
	}

	public onSelectedSegmentChange(segment: string): void {
		this.selectedSegment = segment;
	}

	public async getLoanContractTemplate(): Promise<void> {
		this.loanContractTemplate = await this.loanContractTemplateProvider.getOne();
	}

	public async openCommunicationTemplate(
		type: 'custom' | 'standard',
		templateId?: string,
		messageType?: CommunicationMessageType.CustomLessonReservation | CommunicationMessageType.CustomReservation
	): Promise<void> {
		let template: Partial<ICommunicationTemplate> | undefined;
		let templateVariables: Array<string> | undefined;

		if (type === 'standard') {
			const result = await this.communicationTemplateProvider.getOne(templateId);
			template = result.template;
			templateVariables = result.variables;
		} else if (type === 'custom') {
			templateVariables = await this.communicationTemplateProvider.getVariables(messageType);
			template = templateId
				? (await this.communicationTemplateProvider.getOne(templateId)).template
				: { messageType };
		}

		void this.navigationService
			.navigateTo<{ action: Action }>(NavigationType.page, '/digibank/template', {
				type,
				template,
				templateVariables,
			})
			.catch();
	}

	public async toggleCommunicationTemplateEnabled(
		template: ICommunicationTemplate,
		event: CustomEvent
	): Promise<void> {
		if (!event.detail.checked) {
			if (!(await this.validationService.actionIsConfirmed('DISABLE_COMMUNICATION_TEMPLATE'))) {
				template.isEnabled = true;
				return;
			}
		}

		await this.communicationTemplateProvider.updateIsEnabled(template.id, event.detail.checked);
		await this.getAllCommunicationTemplates();
	}

	public async updateLoanContractTemplate(): Promise<void> {
		const html = this.editor.getFormattedHtml();
		this.loanContractTemplate.template = await this.loanContractTemplateProvider.createOrUpdate(
			this.loanContractTemplate.template.id,
			{ html }
		);
		this.messageService.showSavedMessage();
	}

	public async update(): Promise<void> {
		if (formIsValid(this.tenantForm, TenantDesktopPage)) {
			const tenant = omit(this.tenantForm.value, 'logo', 'favicon', 'intro');
			for (const key of Object.keys(tenant)) {
				this.tenantFormData.set(key, JSON.stringify(tenant[key]));
			}
			this.tenantFormData.set('intro', this.editor.getFormattedHtml());
			await this.provider.update(this.tenantFormData);
			this.messageService.showSavedMessage();
			this.refreshUserDetails();
		}
	}

	/**
	 * Add the selected file to the FormData form and add the image version
	 * to the reactive form to show to the user
	 * @param type The type of image (logo or favicon)
	 * @param event The browser event
	 */
	public async addImageToForm(type: 'logo' | 'favicon', event: Event): Promise<void> {
		const file: File = (event.target as HTMLInputElement).files?.[0];
		if (file) {
			const [image, filename] = await this.fileService.convertToImage(file);
			this.tenantForm.get(type).setValue(image);
			this.organizerFormData.set(type, file, filename);
		}
	}

	/**
	 * Select an organizer and link it to this tenant
	 * @param organizer The organizer to link
	 */
	public async selectUiTDatabaseOrganizer(organizer: IUiTDatabaseOrganizer): Promise<void> {
		const formattedOrganizer = {
			id: organizer['@id'],
			name: this.organizerNamePipe.transform(organizer.name),
			website: organizer.url,
		};

		await this.uiTDatabaseProvider.linkOrganizer(formattedOrganizer);
		this.tenant.Organizer = formattedOrganizer;
		this.messageService.showSavedMessage(translate('TENANT.UIT_DATABASE.ORGANIZER_LINKED'));
	}

	/**
	 * Remove a selected organizer and unlink it to the tenant
	 */
	public async removeSelectedUiTDatabaseOrganizer(): Promise<void> {
		if (await this.validationService.actionIsConfirmed('REMOVE_ORGANIZER')) {
			await this.uiTDatabaseProvider.unlinkOrganizer();
			this.tenant.Organizer = null;
			this.organizerImageForm.reset();
			this.messageService.showSavedMessage(translate('TENANT.UIT_DATABASE.ORGANIZER_UNLINKED'));
		}
	}

	/**
	 * Create a new organizer and link it to the tenant
	 */
	public async createAndSelectUiTDatabaseOrganizer(): Promise<void> {
		if (formIsValid(this.organizerForm)) {
			const organizer = await this.uiTDatabaseProvider.createOrganizer(this.organizerForm.value);
			await this.selectUiTDatabaseOrganizer(organizer);
		}
	}

	/**
	 * An event handler that is fired when the hasSearched property of the organizer-search component is fired.
	 * @param hasSearched The hasSearched property value
	 */
	public async hasSearchedUiTDatabaseOrganizerChanged(hasSearched: boolean) {
		this.hasSearchedUiTDatabaseOrganizer = hasSearched;
	}

	/**
	 * Add the selected file to the organizer FormData form and add the image
	 * to the reactive form to show to the user
	 * @param event The browser event
	 */
	public async updateOrganizerImage(event: Event): Promise<void> {
		try {
			const file: File = (event.target as HTMLInputElement).files?.[0];
			if (file) {
				const [image, filename] = await this.fileService.convertToImage(file);
				this.organizerImageForm.get('image').setValue(image);
				this.organizerFormData.set('image', file, filename);

				await this.uiTDatabaseProvider.updateImage(this.organizerFormData);
				this.messageService.showSavedMessage();
			}
		} catch (err) {
			this.organizerImageForm.get('image').setValue(null);
			this.organizerFormData.delete('image');
			this.errorService.showApplicationError(
				translate('TENANT.UIT_DATABASE.IMAGE_SELECT_ERROR'),
				ErrorType.Component
			);
		}
	}

	/**
	 * Create or update a uit database label
	 * @param uitDatabaseLabel The optional label to update
	 */
	public async createOrUpdateUiTDatabaseLabel(uitDatabaseLabel?: IUiTDatabaseLabel): Promise<void> {
		void this.navigationService
			.navigateTo<{ action: Action }>(NavigationType.modal, UiTDatabaseLabelComponent, {
				uitDatabaseLabel,
				action: uitDatabaseLabel ? Action.UPDATE : Action.CREATE,
			})
			.then((result) => {
				if (result?.action) {
					void this.getUiTDatabaseLabels();
					this.messageService.showSavedMessage();
				}
			})
			.catch();
	}

	/**
	 * Delete a uit database label
	 * @param uitDatabaseLabel The label to delete
	 */
	public async deleteUiTDatabaseLabel(uitDatabaseLabel: IUiTDatabaseLabel): Promise<void> {
		if (await this.validationService.actionIsConfirmed('UIT_DATABASE_LABEL')) {
			await this.uitDatabaseLabelProvider.delete(uitDatabaseLabel.id);
			void this.getUiTDatabaseLabels();
			this.messageService.showSavedMessage();
		}
	}

	/**
	 * Get the user details from the api and update the local saved details
	 * Because the logo is possibly updated, so we refresh the user details
	 * to propagate those changes
	 */
	private async refreshUserDetails(): Promise<void> {
		const userDetails = await this.userProvider.getDetails();
		this.usersManager.setUserDetails(userDetails);
	}

	/**
	 * Get all communication templates from the database and format them for the UI
	 */
	private async getAllCommunicationTemplates(): Promise<void> {
		const communicationTemplates = await this.communicationTemplateProvider.getAll();
		const groupedTemplatesByMessageKey = groupBy(
			communicationTemplates,
			(template) => `${template.customName ? template.customName : template.messageType}`
		);

		const messageTypeKeys = Object.keys(cloneDeep(groupedTemplatesByMessageKey));

		const getCommunicationTemplatesOfGroup = (group: string) =>
			remove(
				messageTypeKeys,
				(key) => key.includes(group) || groupedTemplatesByMessageKey[key]?.[0]?.messageType.includes(group)
			)
				.sort()
				.map((key) => ({
					[key]: orderBy(groupedTemplatesByMessageKey[key], ['channel', 'endpoint']),
				}));

		this.groupedCommunicationTemplates = {
			rating: getCommunicationTemplatesOfGroup('rating'),
			lesson_reservation: getCommunicationTemplatesOfGroup('lesson-reservation'),
			reservation: getCommunicationTemplatesOfGroup('reservation'),
			beego_appointment: getCommunicationTemplatesOfGroup('beego-appointment'),
			loan: getCommunicationTemplatesOfGroup('loan'),
		};
	}

	/**
	 * Get all UiTDatabase labels from the database
	 */
	private async getUiTDatabaseLabels(): Promise<void> {
		this.uitDatabaseLabels = await this.uitDatabaseLabelProvider.getAll();
	}
}
